import React, { useMemo, useState, useRef, useLayoutEffect } from 'react';
import { useCombobox } from 'downshift';
import classNames from 'classnames';
import { Icon } from 'js/components';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';

function userGroupToLabel(userGroup) {
  return userGroup ? userGroup.name || String(userGroup.id) : '';
}

export function getUserGroupFilterLabel(userGroups = [], selectedIds = []) {
  if (userGroups.length === 0) {
    return '';
  }
  if (selectedIds.length === 0) {
    return 'No user groups';
  }
  if (selectedIds.length === userGroups.length) {
    return 'All user groups';
  }
  if (selectedIds.length === 1) {
    const ug = userGroups.find((g) => g.id === selectedIds[0]);
    return userGroupToLabel(ug) || '1 user group';
  }
  return `${selectedIds.length} of ${userGroups.length} user groups`;
}

export default function UserGroupFilterCombobox({
  placeholder = 'Select or search user groups…',
  userGroups = [],
  selectedIds = [],
  onChange,
  disabled = false,
  id: inputId,
}) {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const cursorRef = useRef(null);
  const [cursor, setCursor] = useState(null);

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const displayLabel = useMemo(
    () => getUserGroupFilterLabel(userGroups, selectedIds),
    [userGroups, selectedIds]
  );

  const filteredUserGroups = useMemo(() => {
    const q = inputValue.trim().toLowerCase();
    if (!q) {
      return userGroups;
    }
    return userGroups.filter(
      (ug) =>
        userGroupToLabel(ug).toLowerCase().includes(q) ||
        String(ug.id).toLowerCase().includes(q)
    );
  }, [userGroups, inputValue]);

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    items: filteredUserGroups,
    selectedItem: null,
    inputValue: isFocused ? inputValue : displayLabel || inputValue,
    itemToString: userGroupToLabel,
    onInputValueChange: ({ inputValue: next }) => {
      setInputValue(next ?? '');
    },
    stateReducer: (state, actionAndChanges) => {
      const { type, changes } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true,
            highlightedIndex: state.highlightedIndex,
            inputValue: state.inputValue,
          };
        default:
          return changes;
      }
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem) {
        return;
      }
      const next = selectedIdSet.has(selectedItem.id)
        ? selectedIds.filter((id) => id !== selectedItem.id)
        : [...selectedIds, selectedItem.id];
      onChange(next);
    },
  });

  const downshiftInputProps = getInputProps({
    id: inputId,
    disabled,
    placeholder,
    ref: cursorRef,
  });

  const displayValue =
    isFocused && !inputValue.trim() ? displayLabel : downshiftInputProps.value;

  useLayoutEffect(() => {
    const input = cursorRef.current;
    if (input && cursor != null) {
      input.setSelectionRange(cursor, cursor);
    }
  }, [cursor, displayValue]);

  const inputProps = {
    ...downshiftInputProps,
    value: displayValue,
    onChange: (e) => {
      setCursor(e.target.selectionStart);
      downshiftInputProps.onChange?.(e);
    },
    onFocus: (e) => {
      setIsFocused(true);
      downshiftInputProps.onFocus?.(e);
    },
    onBlur: (e) => {
      setIsFocused(false);
      setInputValue('');
      downshiftInputProps.onBlur?.(e);
    },
  };
  const toggleProps = getToggleButtonProps({ disabled });

  const selectAll = () => {
    onChange(userGroups.map((ug) => ug.id));
  };

  const clearAll = () => {
    onChange([]);
  };

  return (
    <div className="combobox">
      <div className="input-group limited-width-lg">
        <input
          placeholder={placeholder}
          className="form-control"
          {...inputProps}
        />
        <div className="input-group-append">
          <span aria-label="toggle menu" type="button" {...toggleProps}>
            {isOpen ? (
              <Icon icon={['far', 'chevron-up']} fixedWidth />
            ) : (
              <Icon icon={['far', 'chevron-down']} fixedWidth />
            )}
          </span>
        </div>
      </div>
      <ul
        {...getMenuProps()}
        className="combobox-items"
        style={!isOpen ? { display: 'none' } : {}}
      >
        {isOpen && (
          <>
            <li
              className="combobox-plain-item"
              onMouseDown={(e) => e.preventDefault()}
            >
              <button
                type="button"
                className="btn btn-link btn-xs p-0 mr-3"
                onClick={selectAll}
              >
                Select all
              </button>
              <button
                type="button"
                className="btn btn-link btn-xs p-0 mr-3"
                onClick={clearAll}
              >
                Clear
              </button>
            </li>
            {filteredUserGroups.length === 0 && (
              <li className="combobox-item text-muted">No user groups</li>
            )}
            {filteredUserGroups.map((item, index) => {
              const isSelected = selectedIdSet.has(item.id);
              return (
                <li
                  className={classNames({
                    highlighted: highlightedIndex === index,
                    'combobox-item': true,
                  })}
                  key={`${item.id}-${index}`}
                  {...getItemProps({ item, index })}
                >
                  <span
                    className="mr-2 d-inline-flex"
                    style={{ width: '1rem', justifyContent: 'center' }}
                  >
                    {isSelected && <Icon icon={faCheck} />}
                  </span>
                  <span>{userGroupToLabel(item)}</span>
                </li>
              );
            })}
          </>
        )}
      </ul>
    </div>
  );
}
